import { useEffect, useMemo, useState } from "react";
import { Label, Multiselect, Spinner } from "@osago/ui";
import { getInsurers, type Insurer } from "@/shared/lib/insurers";

interface CompaniesFieldProps {
  selected: string[];
  cleared?: boolean;
  onChange: (codes: string[], cleared: boolean) => void;
}

export function CompaniesField({ selected, cleared = false, onChange }: CompaniesFieldProps) {
  const [insurers, setInsurers] = useState<Insurer[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;

    getInsurers()
      .then((items) => {
        if (alive) setInsurers(items);
      })
      .catch(() => {
        if (alive) setError("Не удалось загрузить список страховых компаний");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  const allSelected = !cleared && !selected.length;
  const value = useMemo(
    () => (allSelected ? insurers.map((i) => i.code) : selected),
    [allSelected, insurers, selected],
  );

  const triggerLabel = useMemo(() => {
    if (allSelected) return "Все СК";
    if (!selected.length) return "Не выбрано";
    if (selected.length === 1) {
      const found = insurers.find((i) => i.code === selected[0]);
      return found ? found.title : "1 СК";
    }
    return `${selected.length} СК`;
  }, [allSelected, selected, insurers]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <span className="block h-8 w-8">
          <Spinner />
        </span>
      </div>
    );
  }

  if (error) {
    return <Label variant="hint">{error}</Label>;
  }

  const isEmpty = cleared && !selected.length;

  return (
    <>
      <Multiselect
        triggerLabel={triggerLabel}
        options={insurers}
        value={value}
        getOptionKey={(i) => i.code}
        getOptionLabel={(i) => i.title}
        selectAllLabel="Все СК"
        searchPlaceholder="Поиск по названию"
        hasError={isEmpty}
        onChange={(next) =>
          onChange(next.length === insurers.length ? [] : (next as string[]), next.length === 0)
        }
      />
      {isEmpty && (
        <Label variant="error" className="mt-1.5 block">
          Не выбрано ни одной СК — отметьте «Все СК» или хотя бы одну компанию
        </Label>
      )}
    </>
  );
}
